'use client';

import * as React from 'react';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';

function boundaryErrorMessage(error: Error): string {
  const msg = error.message?.trim();
  return msg !== '' ? msg : 'An unexpected error occurred.';
}

export interface ChartErrorBoundaryProps extends React.PropsWithChildren {
  /** When this value changes, the boundary resets so children can render again. */
  remountKey: number;
  /** Called when the user activates Retry after a render error (parent should bump `remountKey`). */
  onRemountRequest: () => void;
  /** Report render errors to logging/monitoring (fires when the boundary catches an error). */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ChartErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors from chart/Recharts children so the rest of the page keeps working.
 * Retry clears the boundary by asking the parent to bump `remountKey`.
 */
export class ChartErrorBoundary extends React.Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(prevProps: ChartErrorBoundaryProps) {
    if (prevProps.remountKey !== this.props.remountKey) {
      this.setState({ error: null });
    }
  }

  private handleRetry = () => {
    this.props.onRemountRequest();
  };

  override render() {
    const { error } = this.state;
    if (error) {
      return (
        <Flex
          direction='column'
          align='center'
          justify='center'
          gap='md'
          className='w-full rounded-lg border border-neutral-200 bg-neutral-50 p-d3'
          role='alert'
        >
          <Icon icon='circle-alert' size='lg' color='error' aria-hidden />
          <Text
            variant='b2'
            align='center'
            weight='semibold'
            className='text-neutral-900'
          >
            Unable to display this chart
          </Text>
          <Text variant='l1' align='center' className='text-neutral-600'>
            {boundaryErrorMessage(error)}
          </Text>
          <Button
            type='button'
            variant='primaryFill'
            onClick={this.handleRetry}
          >
            Try again
          </Button>
        </Flex>
      );
    }
    return this.props.children;
  }
}
